<template lang="pug">
PagePeriod(
  :bannerImage="require(`./Opplysningstid_01.jpg`)"
  :circleImage="require(`./sirkel_opplysning.png`)"
)
  // Content here

  // * Tida og samfunnsforholdene
  h3 Tida og samfunnsforholdene

  figure
    img(src="./Opplysningstid_Tida-og-samfunnsforholdene1.jpg" alt="Krigføring utenfor en borg med røyk i bakgrunn. Maleri.")
    figcaption Stormingen av Bastillen 14. juli 1789  innledet den franske revolusjonen

  h5 Borgerskapet vokser fram
  p
    | Et handelsborgerskap vokser fram, og det blir lettere å slå seg opp ved egne krefter. Borgerskapet får dessuten del i den nye kunnskapen som utvikler seg.
  
  h5 Opprør og revolusjon
  p
    | Kongen og adelen har fortsatt så godt som all makt og mange særretter. Men handelen med utlandet øker, og handelsstanden får en stadig større og viktigere posisjon. Borgerne er misfornøyde med de få rettighetene de har. I Frankrike topper misnøyen seg med opprør og revolusjon i 1789. Kongen blir avsatt og seinere henrettet, sammen med mange adelige.

  div.flex.flex-wrap
    div.col.col4
      figure.figure-left.mt5
        img(src="./Opplysningstid_Tida-og-samfunnsforholdene2.jpg" alt="Portrettbilete av mann med langt hår. Måleri.")
        figcaption Isaac Newton
    div.col.col8
      h5 Naturvitenskapen skaper framtidsoptimisme
      p
        | Tankegods fra middelalderen må vike for nye oppdagelser i naturvitenskapen. Newtons lover blir avgjørende for mekanikken, som etter hvert legger grunnlaget for industrialisering og masseproduksjon. 

  // * Tanker i tida
  h3 Tanker i tida

  figure.col.col5.right-image.mt5
    img(src="./Opplysningstid_Tanker_i_tida.jpg" alt="Kvinner med blomsterkranser i håret sitter i grupper. Lysstråler uthever den øverste kvinnen. Illustrasjon.")
    figcaption Detalj fra omslaget til Encyclopédie (1772), tegnet av Charles-Nicolas Cochin og gravert av Bonaventure-Louis Prévost. Bildet er fylt med symbolisme: Figuren i midten er sannheten, omgitt av et skinnende lys (det sentrale symbolet i opplysningstida). Figurene til høyre er fornuft og filosofi, som river ned sløret foran sannheten.
  h5 Fokus på enkeltmennesket
  p
    | Ideen om at mennesket har grunnleggende rettigheter, uavhengig av stand og klasse, vokser fram. Disse synspunktene finner vi igjen i den norske grunnloven i 1814.
  h5 Rasjonalisme
  p
    | ”Cogito, ergo sum.” (”Jeg tenker, altså er jeg.”) Dette er grunntanken i Descartes’ filosofi. Å være rasjonell eller tenke rasjonelt har med menneskelig fornuft å gjøre. Rasjonalismen ser fornuften og tanken som grunnlaget for all kunnskap. Hos Holberg finner vi den samme tenkemåten: Jeg tviler, altså tenker jeg. For å kunne tro må en tvile. (Holberg-sitat)
  h5 Empirisme
  p
    | Empirismen henger sammen med rasjonalismen. Empiristene mener at erfaring er den eneste sikre kilden til all kunnskap. Empiri betyr noe som er erfart, og vi finner ordet igjen i empirisk forskning. Idéhistorisk legger begge retningene et felles grunnlag for tenkemåten i opplysningstida.
  h5 Menneskesyn
  p
    | Tankene til Descartes og Holberg er idéhistorisk viktige fordi de plasserer grunnlaget for en forståelse av verden i mennesket selv – altså i den menneskelige fornuften, ikke i religiøs tro. Det er i ferd med å vokse fram en oppfatning av at mennesket er et rasjonelt, tenkende vesen.
  h5 Fordommer og autoritetstro blir utfordret
  p
    | Opplysningsfilosofer og forfattere som Holberg i Danmark og Voltaire og Montesquieu i Frankrike kritiserer sterkt fordommer og gammel autoritetstro. Gjennom opplysning skal folk lære seg å tenke selv, slik at de kan utvikle seg til selvstendige individer. 
  h5 Et opplyst samfunn
  p
    | Kunnskap og fornuft bør spres ut til folk for å sikre et opplyst samfunn. Encyclopédie, det første oppslagsverket i verden, kommer ut med hele 35 bind i perioden i 1751–1780. Det er opplysningstanken som er drivkraften bak verdens første leksikon.

  // * Om Stilen
  h3 Om stilen – klassisismen

  figure.col.col6.right-image
    img(src="./Opplysningstid_palladios-Villa-Rotonda.jpg" alt="Herskapelig bygg med bred trapp og høye søyler. Foto.")
    figcaption Palladios Villa Rotonda i Italia

  h5 Det klassiske idealet
  p
    | Den klassisistiske stilen utvikler seg i Frankrike i overgangen mellom 1600- og 1700-tallet. Betegnelsen klassisistisk viser til idealet forfatterne prøver å leve opp til, nemlig de klassiske greske tragediene. Begrepet klassisisme gjelder den kunstneriske uttrykksformen, mens ordet opplysningstid peker mot tida og samfunnsforholdene.
  h5 Idealet: harmoni og måtehold
  p
    | Klassisismen er en reaksjon på de sterkt overdrevne effektene i barokkstilen. Idealet er harmoni og mer måtehold i bruken av virkemidler.

  figure
    img(src="./Opplysningstid_Klassisistisk-arkitektur-Jenisch-Haus.jpg" alt="Symmetrisk bygning med dekorativ balkong og tak. Foto.")
    figcaption Klassisistisk arkitektur: Jenisch Haus i Hamburg

  h5 Sammenfall i tid, sted og handling
  p
    | Diktekunst blir regnet for handverk. Det er klare regler for hvordan en skal skrive i de ulike sjangrene. Det klassiske dramaet følger regelen om sammenfall i tid, sted og handling.

  h3 Litteraturen
  figure.col.col5.left-image
    img(src="./Opplysningstid_Litteraturen1.jpg" alt="Portrett av en mann med krøllete hår. Maleri.")
    figcaption Ludvig Holberg
  p
    | Ludvig Holberg ble født i Bergen, der han også vokste opp. I ung alder reiste han til København for å studere. Seinere ble han dansk-norsk forfatter og professor i metafysikk ved universitetet i København. Holberg var den største og viktigste dansk-norske forfatteren på 1700-tallet. I tillegg var han en viktig folkeopplyser og pedagog.
  h5 En allsidig forfatter
  p
    | Holberg skrev komedier, vitenskapelige avhandlinger, fortellinger på rim, prosafortellinger, brev, essay og satire. Satire er en tekst som kritiserer eller gjør narr av personer eller tilstander i samfunnet. I det satiriske heltediktet ”Peder Paars” laget Holberg en parodi på de mest kjente verkene i latinsk litteratur.
  h5 Karakterkomedier
  p
    | Holbergs komedier følger klassisistiske regler, og vi kaller dem karakterkomedier. Hovedpersonene har gjerne en dårlig side eller en last som fører til konflikt. Dermed utvikler det seg intriger. Til slutt går hovedpersonen på et nederlag og må lære av sine feil.
  h5 Kamuflert samfunnskritikk
  p
    em Niels Klims underjordiske reise 
    | er en sciencefictionroman – på latin! Reisen ender i ideallandet Potu (Utopia), der adelen er ryddet av veien, og bøndene er de mest fornemme. På den måten lanserer Holberg en kamuflert samfunnskritikk.

  NdlaAudio(
    source="media/periods/opplysning/Holberg_Erasmus_Montanus_nrk_lesekunst.mp3"
    description="Erasmus Montanus"
    )
    blockquote
      p Utdrag fra Ludvig Holbergs drama 
        em Erasmus Montanus 
      strong Per Klokker: 
      p De tror at jeg er en hederlig og bra mann og klokker her i sognet, øh –&#8201;hvilket er sant.  
      strong Montanus: 
      p Og jeg påstår at det er &ndash;&#8201;løgn! Jeg påstår at De er en hane, og skal bevise det så klart som at to og tre er fem.  
      strong Per Klokker: 
      p De skal bevise &ndash;&#8201;Fanden skal De! Jasså, er jeg en hane. Æh – hvordan vil De godtgjøre det? 
      strong Montanus: 
      p Kan De si meg noe som skulle forhindre at De er en hane? 
      strong Per Klokker: 
      p For det første kan jeg snakke. En hane kan ikke snakke. Ergo er jeg ikke noen hane. 
      strong Montanus: 
      p Det sier ingenting. En papegøye kan også snakke. Men den er da ikke noe menneske for det.  
      strong Per Klokker: 
      p Jeg kan bevise det på en annen måte også: En hane har ikke menneskelig forstand! Jeg har menneskelig forstand, ergo er jeg ikke noen hane. 
      strong Montanus: 
      p Proba minorem! 
      strong Per Klokker: 
      p Ha, snakk så en kan forstå det! 
      strong Montanus: 
      p Jeg vil at han skal bevise at han har forstand som et menneske. 
      strong Per Klokker: 
      p Æh – jeg skjøtter min stilling upåklagelig. 
      strong Montanus: 
      p Hvilke gjøremål har De som beviser at De har forstand som et menneske? 
      strong Per Klokker: 
      p For det første glemmer jeg aldri å ringe til bestemte tider i kirken! 
      strong Montanus: 
      p En hane glemmer heller aldri å gale og varsle folk når det er på tide å stå opp. 
      strong Per Klokker: 
      p For det andre synger jeg like godt som noen kirkesanger i hele landet! 
      strong Montanus: 
      p Hanen vår galer like godt som noen hane i landet. 
      strong Per Klokker: 
      p Jeg kan støpe vokslys! Det kan ikke en hane gjøre! 
      strong Montanus: 
      p Men en hane kan lage egg, og det kan ikke De gjøre! Innser De nå at De ikke har motbevist at De ikke er en hane? Jeg skal derimot nevne et par utpregede likhetspunkter: En hane har en kam på hodet. Det har De også. En hane galer. Det gjør De også! En hane skryter av stemmen sin og brysker seg. Det gjør De også! Og en hane spankulerer omkring uten å bestille noe mellom hver gang han galer. Og det gjør De også! Ergo er De en hane! –&#8201;Har De ellers noe å si? 
      p (Per Klokker begynner å gråte.) 
      strong Jesper Ridefogd: 
      p Så, så, ikke gråt da, Per Klokker! Du skal ikke bry deg om hva han sier.  
      strong Per Klokker: 
      p Ja, men det er jo ren og skjær løgn, så sant Gud hjelpe meg! Jeg kan skaffe attest fra hele prestegjeldet om at jeg ikke er en hane, eller at noen av mine foreldre har vært annet enn kristne mennesker!  
      p (Per Klokker gråter enda høyere.) 
      strong Programleder: 
      p Dette er en overstadig komedie av Ludvig Holberg. Hva er dette egentlig for slags dialog? 
      strong Gjest i programmet: 
      p Ja, det utdraget som vi nå hørte fra "Erasmus Montanus", det dreier seg om at Erasmus, eller Rasmus Berg, som han jo egentlig heter, har gått på latinskolen og lært seg latin og retorikk, altså kunsten å tale og kunsten å overtale. Og her overtaler han Per Klokker, stakkar, til å tro at han er en hane.  
      p Han gjør det med alle retorikkens virkemidler, men da særlig med ett virkemiddel, det som vi kaller en falsk syllogisme eller et entymem: Han sammenligner hele tida Pers virksomhet med hanens virksomhet. Så trekker han da en feilslutning mellom de to tingene, men den virker likevel retorisk overbevisende, selv om det altså ikke er sant at klokkeren er en hane. Og dét er et grunnproblem i hele retorikken: forholdet mellom sant og usant. Er det sånn at den som overbeviser en annen om noe, har rett i det han sier? 
          

  NdlaAudio(
    source="media/periods/opplysning/Jeppe_på_Bjerget_med_Hauk_Aabel.mp3"
    description="Jeppe på Bjerget"
  )
    blockquote
      p Utdrag fra Ludvig Holbergs drama 
        em Jeppe på Bjerget 
      strong Jeppe: 
      p Nei, hva er dog dette? Hva er dette for en herlighet? Og hvordan er jeg kommet hit? 
      p (Jeppe gråter.)  

      p Drømmer jeg, eller er jeg våken? Nei, jeg er ganske våken. Hvor er min kone? Hvor er mine barn? Hvor er mitt hus, og hvor er Jeppe? Allting er jo forandret. Jeg selv med. Nei, hva er nå dette?  
      p (Jeppe gråter.) 
      p Nille? Nille? Jeg tror jeg er kommet i himmelrik, og det ganske uforskyldt.  
      p (Han gråter og ler.) 
      p Men mon det, er jeg? Meg synes ja. Meg synes også nei. Når jeg føler på min rygg som ennå er øm av de hugg den har fått, ja, og når jeg hører meg tale. Når jeg føler på min hule tann, synes meg det er jeg. Når jeg derimot ser på min lue, min skjorte, på all den herlighet som er meg for øynene, og jeg hører den liflige musikk, så droll splide meg ad om jeg kan få i mitt hode at det er jeg. Nei, det er ikke jeg. Heller tusen ganger være kanalje, om det er.  
      p (Jeppe gråter.) 
      p Men, mon jeg ikke drømmer? Jeg vil forsøke å knipe meg i armen. Gjør det da ikke ondt, så drømmer jeg. Gjør det ondt, så drømmer jeg ikke. – Jo, jeg følte det, jeg er våken! Visst er jeg våken, det kan jo ingen disputere meg. Hvis jeg ikke var våken, så kunne jeg ... 
      p Men hvorledes kan jeg dog være våken når jeg rett ettertenker allting? Det kan ikke slå feil at jeg jo er Jeppe på Bjerget? En vet jo at jeg er en fattig bonde. En trell, en slyngel, en hanrei, en sulten lus, en madit, en kanalje! 
      p Hvorledes kan jeg da til like med være keiser og herre på et slott? Nei, det er ikke mulig, det må derfor kun være en drøm. Jeg må derfor ha tålmodighet til jeg våkner opp igjen. 
      p Skuespilleren kommenterer handlingen: 
      p Så legger han seg til å sove, men så begynner musikken igjen, og så faller han i gråt. 
      strong Jeppe: 
      p Hvorledes kan man dog høre sånt i søvne? Det er jo ikke mulig, det? 
      p Men er det en drøm, så gid jeg aldri måtte våkne opp igjen! Og er jeg gal, så gid jeg aldri måtte bli klok igjen! Jeg ville stevne den doktoren der kurerte meg og forbanne den der vekket meg. Jeg verken drømmer eller er gal, ti jeg kan jo komme all ting i hu som meg er vederfaret. 
      p Jeg erindrer at min salig far var Niels på Bjerget, min farfar Jeppe på Bjerget. Min hustru heter jo Nille og hennes krabat Mester Erik. Mine tre sønner Jens, Niels og Krestoffer. 
      p (Ler) 
      p Men se, nu har jeg funnet ut hva det er.  
      p Åh, nei, det er det annet liv. Det er paradis. Det er himmelrik. Åh, nei ... Åh.  
      p Jeg måskje drakk meg i hjel i går hos Jacob Skomaker. Døde – og kom straks i himmelrik. Døden må dog ikke være så hård å gå på som man biller seg inn. Ti jeg følte intet dertil.  
      p Nu står måskje herr Jesper denne stund på prekestolen og holder likpreken over meg og sier: 
      p (Jeppe gjør seg til og snakker som en prest.) 
      p "Sådant endelikt hadde Jeppe på Bjerget. Han levet som en soldat og han døde som en soldat."  
      p (Ler) 
      p Man kan disputere om jeg døde til lands eller til vanns – for jeg gikk temmelig fuktig av denne verden.  
      p (Jeppe og publikum ler.)  
      p Akk, Jeppe, det er annet enn å gå fire mil til byen etter såpe, ligge på strå, få hugg av din hustru og horn av degnen. Akk, til hvilken lykksalighet er ikke din møye og dine sure dager forvandlet. Jeg må gråte av glede. Besynderlig når en betenker at det er skjedd meg så uforskyldt. 
      p Men én ting står meg for hodet, og det er – jeg er så tørstig – at mine lepper henger sammen. Skulle jeg ønske meg levende igjen, så var det alene for å få et krus øl å leske meg på. Ti hva nytte med all den herlighet for øynene og for ørene når jeg skal dø på ny igjen av tørst? Jeg erindrer presten har jo ofte sagt at man verken hungrer eller tørster i himmelrik. I like måte at man der treffer alle sine avdøde venner. Jeg er jo ferdig til å vansmekte av tørst. Jeg er jo også ganske alene, ti jeg ser – jeg ser intet menneske. Jeg måtte jo i det ringeste finne min bestefar. Han var slik skikkelig mann der ikke etterlot seg en skillings restanse hos herskapet.  
      p Jeg vet jo at mange folk har levet like så skikkelig som jeg. Hvorfor skulle jeg alene komme i himmelrik? Det kan derfor ikke være himmelrik. Hva kan det da være? Jeg sover ikke, og jeg er ikke våken, og jeg er ikke død, og jeg lever ikke, og jeg er ikke gal og jeg er ikke klok. Jeg er Jeppe på Bjerget, og jeg er ikke Jeppe på Bjerget, jeg er fattig, jeg er rik, jeg er en stakkars, og jeg er keiser og ... Åh, hjelp, hjelp! 
      p Skuespilleren kommenterer neste scene: 
      p Og så kommer de inn og holder ... (Resten av setningen er uforståelig.) 
      p (Publikum ler.) 

  .clear
  figure
    img(src="./Opplysningstid_Litteraturen2.jpg" alt="Herskapelige mennesker samla rundt noen bord. Maleri.")
    figcaption 
      em En aften i Det norske Selskab
      | , Eilif Peterssen

  NdlaAudio(
    source="media/periods/opplysning/Norges_skaal.mp3"
    description="Norges Skaal"
  )
    blockquote
      p
        | For Norge, Kiæmpers Fødeland,
        br
        | Vi denne Skaal vil tømme,
        br
        | Og naar vi først faae Blod paa Tand,
        br
        | Vi sødt om Frihed drømme;
        br
        | Dog vaagne vi vel op engang
        br
        | Og bryde Lænker, Baand og Tvang;
        br
        | For Norge, Kjæmpers Fødeland,
        br
        | Vi denne Skaal udtømme!

      p
        | Hver tapper Helt, blandt Klipper fød,
        br
        | Vi drikke vil til Ære;
        br
        | Hver ærlig Norsk, som Lænker brød,
        br
        | Skal evig elsket være!
        br
        | Den vrede Livvagts Vaabenbrag
        br
        | Forklarer trolig Nordmænds Sag.
        br
        | Hver ærlig Norsk, blandt Klipper fød,
        br
        | Vi drikke nu til Ære!

      p
        | En Skaal for Dig, min kjække Ven,
        br
        | Og for de norske Piger!
        br
        | Og har Du en, saa Skaal for den!
        br
        | Og Skam faae den, som sviger!
        br
        | Og Skam faae den, som elsker Tvang
        br
        | Og hader Piger, Viin og Sang!
        br
        | En Skaal for Dig min kjække Ven,
        br
        | Og for de norske Piger!

      p
        | Og nok en Skaal for Norges Fjeld,
        br
        | For Klipper, Snee og Bakker!
        br
        | Hør Dovres Echo raabe: “Held!”
        br
        | For Skaalen tre Gang takker.
        br
        | Ja tre Gang tre skal alle Fjeld
        br
        | For Norges Sønner raabe Held;
        br
        | Endnu en Skaal for Dig mit Fjeld,
        br
        | For Klipper, Snee og Bakker!  


  h5 Norsk studentmiljø i København
  p
    | Norske Selskab blir stiftet i 1772 av en gruppe norske studenter i København. På den tida har ikke Norge eget universitet, og alle nordmenn som vil studere, må reise utenlands.
  h5 Et møtested og en drikkestue
  p
    | Norske Selskab er møtested og ølkafé for norske studenter, men også en litteraturklubb og en dikterstue. Her diktes det muntre drikkeviser, hyllingsdikt og parodier.
  h5 Norges første nasjonalsang
  p
    | En av stifterne er Johan Nordahl Brun, som seinere ble biskop i Bergen. Han er mest kjent for diktet ”For Norge, kjempers fødeland”. Teksten er skrevet som en drikkevise, men ble seinere vår første nasjonalsang.
  h5 Wessel – en satiriker av de sjeldne
  p
    | Johan Herman Wessel (1742–1785) er sentral i Norske Selskab. Han skrev 
    em Kiærlighed uden strømper
    | , en parodi på den klassisistiske tragediediktningen på 1700-tallet. Wessel er også kjent for sine satiriske vers og gravskrifter.
  h5 Flere nordmenn i klubben
  p
    | Andre sentrale navn i Norske Selskab er Johan Vibe, Jens Zetlitz og Claus Fasting.

  h3 Språket 

  h5 Latin – de lærdes språk
  p
    | Latin er de lærdes språk på 1700-tallet. Holberg skrev mange av tekstene sine på latin, og han holdt forelesninger ved universitetet på latin.
  h5 Fransk som diplomatspråk
  p
    | Frankrike er en stormakt. Latin blir derfor avløst av fransk som diplomatspråk. Også i overklassemiljøene rundt om i Europa får det franske språket status.

  h5 Kansellistil
  p
    | Det tysk-latinske stilmønsteret er et ideal. Dette er en uttrykksform med lange ytringer, mange leddsetninger og innskudd. Skrivemåten har seinere fått betegnelsen kansellistil.

  p Eksempel på kansellistil:  
    br 
    a(href="media/periods/opplysning/Egede.jpg" target="_blank") Hans Egede: ”Grønlands Naturell Historie” (1741)
    NdlaAudio(
      source="media/periods/opplysning/tekst5_Egede_gronlands_naturelle_historie.mp3"
    )

  h5 Holberg er kritisk
  p
    | Holberg er kritisk til denne skrivestilen. Han er dessuten kritisk til bruken av lånord av fransk eller latinsk opphav, og han argumenterer for å bruke et enklere og mer talemålsnært skriftspråk. Men de få som er skrivekyndige, bruker embetsspråket, og dermed blir kansellistilen normgivende.
  h5 Dansk påvirkning i byene
  p
    | I Norge får dansk tale status i byene. Det er mange danske embetsmenn med familier her i landet, og dette påvirker utviklingen av talemålet i byene.
  h5 Dialektene holder stand
  p
    | På bygdene og ute i distriktene har dansken liten innflytelse. Her bruker folk dialektene som før.
  h5 Dansk skrift
  p
    | Skriftspråket er dansk, og de som kan skrive, skriver dansk.

  p Eksempel på språket i opplysningslitteraturen: 
    br
    a(href="media/periods/opplysning/Pontoppidan.jpg" target="_blank") Pontoppidan: «Geografisk Oplysning til Kartet over sydlige Norge» (1785) 
    NdlaAudio(
      source="media/periods/opplysning/tekst6_Pontoppidan_Geografisk_beskivelse.mp3"
    )

  h3 Andre kunstformer

  figure
    img(src="./Opplysningstid_Andre_kunstformer2.jpg" alt="En gruppe mennesker i skogen. Maleri.")
    figcaption Maleri av Jean-Antoine Watteau (1721)

  h5 Rokokko
  p
    | Stilretningen som dominerer bildekunst, møbler og arkitektur på 1700-tallet, kaller vi rokokko. Denne stilen bryter med barokkens bruk av overdådige virkemidler. Det er en enklere, mer elegant og mindre følelsesladd stil.
  h5 Ny musikkstil
  figure
    img(src="./Opplysningstiden_Mozart-spiller.jpg" alt="Ei kvinne og to menn ved et orgel. Mannen på høyre side holder en fiolin. Maleri.")
    figcaption Mozart spiller kammermusikk sammen med søsteren og faren.
  p
    | Musikkstilen endrer seg også på 1700-tallet. Uttrykksformen går fra den tunge, kontrastfylte barokkstilen til en lettere og mer elegant form.
  h5 Kammermusikk
  p
    | Komposisjoner for små ensembler passer for framføring i små rom, og kommer på mote. Sonatesatsen, som omfatter tre deler, er en slik kammermusikksjanger. Sonaten er en komposisjon som blir innført etter samme modell som det klassisistiske dramaet.
  h5 Wienerklassisisme
  p
    | Klassisismen i musikken kalles også wienerklassisisme. Det er fordi Wien var hjemstedet til de fleste komponistene på 1700-tallet.

  div.flex.flex-wrap
    div.col.col4
      figure.figure-left
        img(src="./Opplysningstid_Andre_kunstformer4.jpg" alt="Mann med hvit pudderparykk. Maleri.")
    div.col.col8
      h5 To store wienerklassisister
      p
        | Wolfgang Amadeus Mozart (1756–1791) er den største av wienerklassisistene. En annen er Joseph Haydn (1732–1809). Begge er kjent for en stor produksjon av minneverdig musikk som aldri går av mote.
  div.flex.flex-wrap
    div.col.col4
      figure.figure-left
        img(src="./Opplysningstid_Andre_kunstformer5.jpg" alt="Fokusert mann noterer i et hefte. Maleri.")
    div.col.col8
      h5 Beethoven som wienerklassisist
      p
        | De første komposisjonene til Ludwig van Beethoven (1770–1827) har mye til felles med Mozart og Haydn. Denne delen av Beethovens produksjon regner vi derfor som wienerklassisistisk.



</template>

<script>
import PagePeriod from "@/components/PagePeriod";
import NdlaVideo from "@/components/NdlaVideo";
import NdlaAudio from "@/components/NdlaAudio";

export default {
  name: "Book",
  components: {
    NdlaAudio,
    NdlaVideo,
    PagePeriod,
  },
};
</script>
